import { disposeObjectRecursive } from '../helpers';
import type { SceneManager } from '../scene-manager.class';

/**
 * Dispose of all resources
 */
export const disposeCfn = (self: SceneManager, _params: Record<string, never>): void => {
	const scene = self.scene;

	// Dispose the render scheduler (cancels all pending frames)
	const scheduler = self.renderScheduler;
	if (scheduler) {
		scheduler.dispose();
		self.setRenderScheduler(null);
	}

	// Stop render loop (legacy fallback)
	self.stopRenderLoop();

	// Remove resize handler
	const resizeHandler = self.resizeHandler;
	if (resizeHandler) {
		window.removeEventListener('resize', resizeHandler);
		self.setResizeHandler(null);
	}

	// Disconnect container ResizeObserver
	const resizeObserver = self.resizeObserver;
	if (resizeObserver) {
		resizeObserver.disconnect();
		self.setResizeObserver(null);
	}

	// Cancel any pending debounced resize
	const pendingResize = self.resizeDebounceTimer;
	if (pendingResize !== null) {
		clearTimeout(pendingResize);
		self.setResizeDebounceTimer(null);
	}

	// Cancel any pending settle timer
	const pendingSettle = self.resizeSettleTimer;
	if (pendingSettle !== null) {
		clearTimeout(pendingSettle);
		self.setResizeSettleTimer(null);
	}

	// Traverse scene and dispose every GPU resource before removal.
	// scene.clear() only detaches children; geometries/materials/textures
	// otherwise linger until the next GC cycle, which iOS Safari often
	// kills the tab before reaching.
	if (scene) {
		scene.traverse((obj) => disposeObjectRecursive(obj));
	}

	// Clear object holder
	const objectHolder = self.objectHolder;
	if (objectHolder && scene) {
		scene.remove(objectHolder);
		self.setObjectHolder(null);
	}

	// Clear scene
	if (scene) {
		scene.clear();
		self.setScene(null);
	}

	// Remove renderer from DOM and dispose
	const renderer = self.renderer;
	if (renderer) {
		if (renderer.domElement.parentElement) {
			renderer.domElement.parentElement.removeChild(renderer.domElement);
		}
		renderer.renderLists.dispose();
		renderer.dispose();
		// forceContextLoss synchronously releases the WebGL context's texture
		// and buffer pool — iOS Safari needs this to survive session switches.
		renderer.forceContextLoss();
		renderer.domElement.width = 1;
		renderer.domElement.height = 1;
		self.setRenderer(null);
	}

	self.setCamera(null);
	self.setClock(null);
	self.setIsActive(false);
};
